import { Entity } from '@api/api/entity-schema';
import { TabLines } from '@design/components/tab-lines/TabLines';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { ChartPrice } from '@terminal/modules/simple-chart/components/chart-price/ChartPrice';
import { useSnakeMeta } from '@terminal/react-query-hooks/snakes/useSnake';
import { queryKeys } from '@terminal/routes/query-params';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  SubTabsType,
  useEarningsInsightDrawer,
} from '../useEarningsInsightDrawer';
import { DocumentModal } from './components/document-modal/DocumentModal';
import { EntityHeaderWatchlist } from './components/entity-header-watchlist/EntityHeaderWatchlist';
import styles from './EarningsInsightWidget.module.scss';
import { EarningsPreview } from './earnings-preview/EarningsPreview';
import { EarningsRecap } from './earnings-recap/EarningsRecap';

export interface EarningsInsightWidgetProps {
  expandCitations: boolean;
  entity?: Entity;
}

export const EarningsInsightWidget = ({
  expandCitations,
  entity,
}: EarningsInsightWidgetProps) => {
  const { t } = useTranslation(['earnings']);
  const { switchTab, activeTab } = useEarningsInsightDrawer();
  const [resetKey, setResetKey] = useState(0);
  const { params } = useQueryParams();
  const isDrawerExpanded = params[queryKeys.drawerExpanded];

  const { data: snakeMeta } = useSnakeMeta(entity?.default_snake);

  const earningsInsightTabs = [
    {
      tabKey: SubTabsType.Preview,
      tabLabel: t('earnings:earningsInsight.drawer.earningsPreview.title'),
    },
    {
      tabKey: SubTabsType.Recap,
      tabLabel: t('earnings:earningsInsight.drawer.earningsRecap.title'),
    },
  ];

  const handleTabChange = (index: number, _tabKey: SubTabsType) => {
    switchTab(index);
    setResetKey(k => k + 1);
  };

  return (
    <div>
      <DocumentModal />
      <div>
        {entity && !isDrawerExpanded && (
          <div className={styles.entityPriceWrapperUniversal}>
            <EntityHeaderWatchlist entity={entity} logoSize={48} />
            <ChartPrice
              entity={entity}
              snakeMeta={snakeMeta}
              enablePriceHover={false}
              className={styles.chartPriceStyles}
              waitForLiveTick
            />
          </div>
        )}
        <TabLines
          className={styles.tabLines}
          tabs={earningsInsightTabs}
          activeTab={activeTab}
          onChange={handleTabChange}
        />
      </div>
      <ErrorBoundaryWrapper resetKeys={[resetKey]}>
        <>
          {activeTab === SubTabsType.Preview ? (
            <EarningsPreview expandCitations={expandCitations} />
          ) : (
            <EarningsRecap expandCitations={expandCitations} />
          )}
        </>
      </ErrorBoundaryWrapper>
    </div>
  );
};
